import React, { useState, useEffect } from 'react';

import { Typography, Grid, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import graphic1 from '../assets/hispanicWomen20.svg';
import graphic2 from '../assets/blackWomen16.svg';
import graphic3 from '../assets/whiteWomen8.svg';
import graphic4 from '../assets/blackPeople19.svg';
import graphic5 from '../assets/hispanicPeople16.svg';
import graphic6 from '../assets/whitePeople7.svg';
import venusAudio from '../assets/audio/stats2Venus.wav';
import playButton from '../assets/playAudio3.svg';
import pauseButton from '../assets/pauseAudio.svg';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: '3.125rem'
  },
  circleGraphCont: {
    width: '51%',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  graphicSec1: {
    width: '78%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },

  boldFont: {
    fontFamily: 'proximaNovaB'
  },
  infoGraphTit: {
    ...theme.extraFonts.insertTitle,
    width: '42%',
    textAlign: 'center',
    marginTop: '3.125rem',
    [theme.breakpoints.down('sm')]: {
      width: '90%'
    }
  },
  bargraphsCont: {
    width: '51%',
    maxWidth: '600px',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  barsCont: {
    // overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  textAndGraphic: {
    width: '90%',
    marginTop: '1.2rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  infoGraphText: {
    width: '18%',
    textAlign: 'right',
    [theme.breakpoints.down('md')]: {
      width: '80%',
      textAlign: 'left'
    }
  },
  textBoxCont: {
    width: '51%'
  },
  bargraph: {
    marginLeft: '1rem',
    [theme.breakpoints.down('md')]: {
      marginLeft: 0
    }
  },
  textWAudio: {
    width: '51%',
    flexWrap: 'nowrap',
    marginTop: '3.125rem',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  textBlock2: {
    width: '81%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  audioCaptions: {
    width: '100%',
    marginLeft: '3rem'
  },
  audioCaptionCont: {
    width: '13%',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  button: {
    width: '88%',
    padding: 0,
    marginTop: '.75rem',
    '&:hover': {
      transform: 'scale(1.1,1.1)'
    }
  },
  button1: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none'
    }
  },
  //   ------------------------------------------------------------------------------------ mobile
  mobileWrapper: {},
  mobileAudioCapCont: {
    marginTop: '3.125rem',
    // minWidth: '278px',
    width: '75%',
    textAlign: 'center',
    padding: '2rem 0',
    borderTop: '1px solid black',
    borderBottom: '1px solid black'
  },
  buttonMobile: {
    width: '40%'
  },
  mobileCaptionText: {
    height: '150px'
  },
  boldYellow: {
    color: theme.palette.common.yellow,
    fontFamily: 'proximaNovaB'
  }
}));

const venusAudioPlayer = new Audio(venusAudio);
const Stats2 = () => {
  const classes = useStyles();
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesMD = useMediaQuery(theme.breakpoints.down('md'));
  const [isPlaying, setIsPlaying] = useState(false);

  const { ref, inView } = useInView();
  const animation = useAnimation();

  useEffect(() => {
    if (inView) {
      animation.start({
        opacity: 1,
        transition: {
          duration: 3,
          delayChildren: 2,
          staggerChildren: 0.04,
          staggerDirection: -1
        }
      });
    }
    if (!inView) {
      animation.start({
        opacity: 0
      });
    }
  }, [inView, animation]);

  const audioClickHandler = () => {
    if (isPlaying) {
      venusAudioPlayer.pause();
      setIsPlaying(false);
    } else {
      venusAudioPlayer.play();
      setIsPlaying(true);
    }
  };

  const mobileAudioCaption = (
    <Grid container justifyContent="center" className={classes.mobileWrapper}>
      <Grid container justifyContent="center" className={classes.mobileAudioCapCont}>
        <Typography variant="body2" className={classes.mobileCaptionText}>
          “When [people] feel lost, that's when misinformation, misconception and mistrust creeps
          in."
          <br />
          <span className={classes.boldFont}>Venus Gines</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler();
          }}
        >
          <img
            src={isPlaying ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Grid container direction="column" alignItems="center" className={classes.wrapper}>
      <Grid container justifyContent="center" className={classes.circleGraphCont}>
        <Grid
          ref={ref}
          component={motion.div}
          animate={animation}
          container
          direction={matchesMD ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={matchesMD ? 'center' : null}
          className={classes.graphicSec1}
        >
          <motion.img src={graphic1} alt="gradient circle with 20%" style={{ width: '220px' }} />
          <motion.img
            src={graphic2}
            alt="gradient circle with 2x"
            style={{ margin: '1rem 0', width: '188px' }}
          />
          <motion.img src={graphic3} alt="gradient circle with 8%" style={{ width: '128px' }} />
          <Typography variant="body2" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            Black women are <span className={classes.boldFont}>2x</span> as likely to be uninsured
            or to depend on public insurance compared to white women.<sup>15 </sup>
            <span className={classes.boldFont}>20%</span> of Latinas are uninsured, compared to 8%
            of white women. <sup>16</sup>
          </Typography>
        </Grid>
        <Typography variant="body1" style={{ marginTop: '3.125rem' }}>
          Low-income Black and HIspanic women become especially vulnerable because care requires
          them to take time off of their respective jobs and often necessitates a reliance on public
          transportation, increasing their time away from work, decreasing their wages, and making
          them dependent on public infrastructure that is often underfunded and unreliable.
          Additionally, in some communities, women fear speaking to their providers because of
          anxieties about their immigration status.
        </Typography>
      </Grid>
      {/* ---------------------------------------------------------------------------------------------------stat2 */}
      <Grid container direction="column" alignItems="center" className={classes.bargraphsCont}>
        <Typography className={classes.infoGraphTit}>
          People Living Below the Federal Poverty Line (2020){' '}
          <sup style={{ fontFamily: 'proximaNovaReg', fontSize: '.8rem' }}> 17</sup>
        </Typography>
        <Grid container direction="column" alignItems="center" className={classes.barsCont}>
          <Grid
            container
            // alignItems={matchesMD ? 'flex-start' : 'center'}
            alignItems="flex-start"
            className={classes.textAndGraphic}
          >
            <Typography variant="body2" className={classes.infoGraphText}>
              Black People
            </Typography>
            <img
              src={graphic4}
              alt="bar graph 19%"
              className={classes.bargraph}
              style={{ height: '30px' }}
            />
          </Grid>
          <Grid
            container
            alignItems={matchesMD ? 'flex-start' : 'center'}
            className={classes.textAndGraphic}
          >
            <Typography variant="body2" className={classes.infoGraphText}>
              Hispanic People
            </Typography>
            <img
              src={graphic5}
              alt="bar graph 19%"
              className={classes.bargraph}
              style={{ height: '30px' }}
            />
          </Grid>
          <Grid container alignItems="center" className={classes.textAndGraphic}>
            <Typography variant="body2" className={classes.infoGraphText}>
              White People
            </Typography>
            <img
              src={graphic6}
              alt="bar graph 19%"
              className={classes.bargraph}
              style={{ height: '30px' }}
            />
          </Grid>
        </Grid>
      </Grid>
      {/* -------------------------------------------------------------------------------------------------------------- text block */}
      <Grid container alignItems="flex-end" className={classes.textWAudio}>
        <Grid container className={classes.textBlock2}>
          <Typography variant="body1">
            Other socioeconomic and cultural influences that disproportionately affect cancer rates
            in Black and Hispanic people include increased rates of smoking, lack of access to
            nutritious foods due in part due to food deserts, and correlative obesity rates. In some
            cases, as is the case with cervical cancer, there is also a cultural stigma around even
            discussing the condition. Understandably, many of thesefactors, coupled with the unjust
            feeling that these communities are somehow to blame for their own circumstances, has led
            to a <span className={classes.boldYellow}>skepticism </span> in, and alienation from,
            the medical community as a whole, which, in turn, leads to women seeking alternative and
            less reliable methods of care.
          </Typography>
        </Grid>

        <Grid container direction="column" className={classes.audioCaptionCont}>
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.audioCaptions}
          >
            <Typography variant="caption">
              “When [people] feel lost, that's when misinformation, misconception and mistrust
              creeps in."
              <br />
              <span className={classes.boldFont}>Venus Gines</span>
            </Typography>
            <Button
              disableRipple
              disableFocusRipple
              className={classes.button1}
              onClick={() => {
                audioClickHandler();
              }}
            >
              <img
                src={isPlaying ? pauseButton : playButton}
                alt="playButton"
                className={classes.button}
                style={{ width: '100%' }}
              />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {matchesSM ? mobileAudioCaption : null}
    </Grid>
  );
};

export default Stats2;
